import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, PlayCircle, Image as ImageIcon, Film, Lock, CheckCircle2, Loader2, AlertCircle, SkipForward } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const OWNER_EMAIL = 'rohit@eipimedia.com';

/** Translate the backend's terse unlock_reason into a transparent label
 *  showing WHO unlocked the gate and (when relevant) WHEN. Added
 *  2026-06-22 — the legacy label collapsed every override into the
 *  word "owner" which made it impossible to tell a global-admin bypass
 *  from a per-project skip from the bug-report screenshot alone. */
function labelForUnlockReason(progress) {
  const r = progress?.unlock_reason || '';
  const m = progress?.bypass_meta || {};
  if (r === 'global_admin') {
    return `global admin (${m.global_admin_email || OWNER_EMAIL} · request as ${m.requesting_user_email || '?'})`;
  }
  if (r === 'owner_skipped') {
    const who = m.owner_skipped_by ? ` by ${m.owner_skipped_by}` : '';
    const when = m.owner_skipped_at ? ` · ${new Date(m.owner_skipped_at).toLocaleDateString()}` : '';
    return `owner skip${who}${when}`;
  }
  if (r === 'grandfathered') {
    const when = m.grandfathered_at ? ` · ${new Date(m.grandfathered_at).toLocaleDateString()}` : '';
    return `grandfathered${when}`;
  }
  if (r === 'all_uploaded') return 'all shots uploaded';
  // Legacy fallback (pre-2026-06-22 backends would return "owner")
  return r.replace(/_/g, ' ');
}

/**
 * Mock Shoot — Stage 3.5 panel.
 *
 * Forces non-owner users to upload one reference image + one video clip
 * per shot before any downstream Stage 4+ AI generation can run. Owner
 * (rohit@eipimedia.com) bypasses via a Skip button.
 */
export default function MockShootPanel({ projectId, shots, currentUser, onUnlockChange, orientation }) {
  const [view, setView] = useState('grid'); // 'grid' | 'timeline'
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState({}); // { [shotId+kind]: true }
  // Lightbox: image URL currently expanded full-screen, or null when closed.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Local copy of shots so upload/delete/replace updates instantly without
  // waiting for a parent re-fetch. Seeded from the prop, then mutated in
  // place by the API responses (mock_image_url / mock_video_url etc).
  const [localShots, setLocalShots] = useState(shots || []);
  useEffect(() => { setLocalShots(shots || []); }, [shots]);

  // Map project's orientation (configured at project creation) to a Tailwind
  // aspect class so portrait shoots don't get center-cropped into landscape
  // tiles. Defaults to landscape if unknown.
  const aspectClass = (() => {
    const o = (orientation || '').replace(/\s/g, '');
    if (o === '9:16') return 'aspect-[9/16]';
    if (o === '1:1')  return 'aspect-square';
    return 'aspect-video'; // 16:9 default
  })();

  // Close lightbox on Escape — standard a11y expectation for modal overlays.
  useEffect(() => {
    if (!lightboxUrl) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxUrl(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  const isOwner = (currentUser?.email || '').toLowerCase() === OWNER_EMAIL;

  const token = (() => {
    try { return localStorage.getItem('token') || ''; } catch { return ''; }
  })();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/mock-shoot/progress`, { headers: authHeaders });
      if (!r.ok) return;
      const data = await r.json();
      setProgress(data);
      if (onUnlockChange) onUnlockChange(!!data.unlocked);
    } catch (e) { /* silent */ }
  }, [projectId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const sorted = (localShots || []).slice().sort((a, b) => {
    const ao = a.sort_order ?? a.order_index ?? 0;
    const bo = b.sort_order ?? b.order_index ?? 0;
    return ao - bo;
  });

  function patchShot(shotId, patch) {
    setLocalShots(prev => prev.map(s => s.id === shotId ? { ...s, ...patch } : s));
  }

  async function uploadFile(shotId, kind, file) {
    if (!file) return;
    const maxBytes = kind === 'image' ? 25 * 1024 * 1024 : 500 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`File too large (${(file.size / 1048576).toFixed(1)} MB). Max ${kind === 'image' ? '25' : '500'} MB.`);
      return;
    }
    const key = `${shotId}_${kind}`;
    setBusy(prev => ({ ...prev, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/shots/${shotId}/mock-${kind}/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Upload failed');
      }
      const data = await r.json();
      // Patch local copy immediately so the tile flips from + to filled
      // without waiting for a parent re-fetch.
      if (kind === 'image') {
        patchShot(shotId, { mock_image_url: data.mock_image_url });
      } else {
        patchShot(shotId, {
          mock_video_url: data.mock_video_url,
          mock_video_thumbnail_url: data.mock_video_thumbnail_url,
          mock_video_duration: data.mock_video_duration,
        });
      }
      toast.success(`${kind === 'image' ? 'Image' : 'Video clip'} uploaded for shot.`);
      await refresh();
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setBusy(prev => ({ ...prev, [key]: false }));
    }
  }

  async function deleteFile(shotId, kind) {
    if (!window.confirm(`Delete this mock ${kind}?`)) return;
    const key = `${shotId}_${kind}_del`;
    setBusy(prev => ({ ...prev, [key]: true }));
    try {
      const r = await fetch(`${API}/api/shots/${shotId}/mock-${kind}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error('Delete failed');
      // Clear local copy immediately.
      if (kind === 'image') {
        patchShot(shotId, { mock_image_url: null });
      } else {
        patchShot(shotId, {
          mock_video_url: null,
          mock_video_thumbnail_url: null,
          mock_video_duration: null,
        });
      }
      toast.success(`${kind === 'image' ? 'Image' : 'Video'} removed.`);
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(prev => ({ ...prev, [key]: false }));
    }
  }

  async function ownerSkip() {
    if (!window.confirm('Skip Mock Shoot stage for this project? (Owner override)')) return;
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/mock-shoot/skip-owner`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: 'Skip failed' }));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Skip failed');
      }
      toast.success('Mock Shoot skipped — downstream stages unlocked.');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="mock-shoot-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-white">Mock Shoot</h2>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Upload a reference image + a short video clip from your real-world mock shoot for every shot.
            Required before AI sketches, keyframes, and video generation are unlocked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="mock-shoot-view-grid"
            onClick={() => setView('grid')}
            className={`px-3 py-1.5 rounded text-sm ${view === 'grid' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >Grid</button>
          <button
            data-testid="mock-shoot-view-timeline"
            onClick={() => setView('timeline')}
            className={`px-3 py-1.5 rounded text-sm ${view === 'timeline' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >Timeline</button>
          {isOwner && progress && !progress.unlocked && (
            <button
              data-testid="mock-shoot-owner-skip-btn"
              onClick={ownerSkip}
              className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-amber-700/30 border border-amber-600/40 text-amber-200 hover:bg-amber-700/50"
              title="Owner-only: skip this gate"
            >
              <SkipForward className="w-4 h-4" /> Skip (owner)
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {progress && (
        <div className="rounded-lg bg-gray-900/50 border border-gray-800 p-4" data-testid="mock-shoot-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-300">
              {progress.both_uploaded} of {progress.total_shots} shots complete · {progress.images_uploaded} images · {progress.videos_uploaded} videos
            </span>
            {progress.unlocked ? (
              <span className="inline-flex items-center gap-1 text-emerald-400 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4" /> Downstream stages unlocked
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-400 text-sm font-medium">
                <Lock className="w-4 h-4" /> Locked
              </span>
            )}
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${progress.unlocked ? 'bg-emerald-500' : 'bg-cyan-500'}`}
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          {progress.unlock_reason && progress.unlock_reason !== 'incomplete' && (
            <p className="text-xs text-gray-500 mt-2" data-testid="mock-shoot-unlock-detail">
              Unlocked via: {labelForUnlockReason(progress)}
            </p>
          )}
        </div>
      )}

      {/* Views */}
      {view === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mock-shoot-grid">
          {sorted.map(shot => (
            <MockShootTile
              key={shot.id}
              shot={shot}
              busy={busy}
              onUpload={uploadFile}
              onDelete={deleteFile}
              onExpandImage={(url) => setLightboxUrl(url)}
              aspectClass={aspectClass}
            />
          ))}
          {sorted.length === 0 && (
            <div className="col-span-full rounded-lg border border-dashed border-gray-700 p-12 text-center text-gray-500">
              No shots yet — finish Stage 2 (Shot Breakdown) and Stage 3 (Asset Bible) first.
            </div>
          )}
        </div>
      )}

      {view === 'timeline' && (
        <MockShootTimeline projectId={projectId} authHeaders={authHeaders} aspectClass={aspectClass} />
      )}

      {/* Lightbox — click backdrop or X to dismiss, Escape also works (handler above) */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
          data-testid="mock-shoot-lightbox"
        >
          <img
            src={lightboxUrl}
            alt="expanded mock"
            className="max-w-full max-h-full object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
            data-testid="mock-shoot-lightbox-close"
            aria-label="Close"
          >×</button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// One tile in the grid view
// ──────────────────────────────────────────────────────────────────────
function MockShootTile({ shot, busy, onUpload, onDelete, onExpandImage, aspectClass }) {
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const imgBusy = busy[`${shot.id}_image`];
  const vidBusy = busy[`${shot.id}_video`];

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-3 space-y-2" data-testid={`mock-shoot-tile-${shot.id}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono text-cyan-400">{shot.scene_number}-{shot.shot_number}</span>
        <span className="text-gray-500">{shot.duration ? `${shot.duration}s` : ''}</span>
      </div>
      <p className="text-xs text-gray-400 line-clamp-2 min-h-[2.5em]">
        {shot.description || 'No description'}
      </p>

      {/* Image slot */}
      <UploadSlot
        kind="image"
        existingUrl={shot.mock_image_url}
        previewKind="image"
        busy={imgBusy}
        accept="image/*"
        onPick={(f) => onUpload(shot.id, 'image', f)}
        onDelete={() => onDelete(shot.id, 'image')}
        onExpand={onExpandImage}
        inputRef={imgRef}
        testId={`mock-image-${shot.id}`}
        aspectClass={aspectClass}
      />

      {/* Video slot */}
      <UploadSlot
        kind="video"
        existingUrl={shot.mock_video_url}
        previewKind="video"
        thumbUrl={shot.mock_video_thumbnail_url}
        duration={shot.mock_video_duration}
        busy={vidBusy}
        accept="video/*"
        onPick={(f) => onUpload(shot.id, 'video', f)}
        onDelete={() => onDelete(shot.id, 'video')}
        inputRef={vidRef}
        testId={`mock-video-${shot.id}`}
        aspectClass={aspectClass}
      />
    </div>
  );
}

function UploadSlot({ kind, existingUrl, previewKind, thumbUrl, duration, busy, accept, onPick, onDelete, onExpand, inputRef, testId, aspectClass }) {
  const [playing, setPlaying] = useState(false);
  const handleClick = () => inputRef.current?.click();
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onPick(f);
    e.target.value = ''; // allow re-selecting the same file
  };
  // Reset to thumbnail when the video url changes (replace flow)
  useEffect(() => { setPlaying(false); }, [existingUrl]);

  const ratioCls = aspectClass || 'aspect-video';

  if (existingUrl) {
    return (
      <div className="relative rounded border border-emerald-700/40 bg-emerald-900/10 overflow-hidden" data-testid={`${testId}-filled`}>
        {previewKind === 'image' ? (
          <button
            type="button"
            onClick={() => onExpand && onExpand(existingUrl)}
            className={`block w-full ${ratioCls} cursor-zoom-in group`}
            data-testid={`${testId}-expand`}
            title="Click to expand"
          >
            <img src={existingUrl} alt="mock" className="w-full h-full object-cover group-hover:opacity-90 transition-opacity" />
          </button>
        ) : playing ? (
          // Actual inline playback once user clicks the play overlay.
          <video
            src={existingUrl}
            controls
            autoPlay
            playsInline
            className={`w-full ${ratioCls} bg-black object-contain`}
            data-testid={`${testId}-player`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className={`relative w-full ${ratioCls} bg-black flex items-center justify-center group`}
            data-testid={`${testId}-play`}
          >
            {thumbUrl ? (
              <img src={thumbUrl} alt="thumb" className="w-full h-full object-cover" />
            ) : (
              <Film className="w-8 h-8 text-gray-600" />
            )}
            <PlayCircle className="absolute w-10 h-10 text-white/90 drop-shadow-lg group-hover:scale-110 transition-transform pointer-events-none" />
            {duration ? (
              <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] rounded">{duration.toFixed(1)}s</span>
            ) : null}
          </button>
        )}
        <div className="absolute top-1 right-1 flex gap-1">
          <button
            onClick={handleClick}
            disabled={busy}
            className="px-2 py-1 bg-black/70 hover:bg-black text-white text-xs rounded"
            data-testid={`${testId}-replace`}
          >Replace</button>
          <button
            onClick={onDelete}
            disabled={busy}
            className="p-1 bg-black/70 hover:bg-red-900 text-red-300 rounded"
            data-testid={`${testId}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <input ref={inputRef} type="file" accept={accept} onChange={handleChange} className="hidden" />
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={`w-full ${ratioCls} rounded border-2 border-dashed border-gray-700 hover:border-cyan-600 hover:bg-cyan-950/20 flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-cyan-400 transition-colors disabled:opacity-50`}
      data-testid={`${testId}-add`}
    >
      {busy ? (
        <Loader2 className="w-6 h-6 animate-spin" />
      ) : (
        <>
          <span className="text-2xl leading-none">+</span>
          <span className="text-xs">{kind === 'image' ? 'Add mock image' : 'Add video clip'}</span>
        </>
      )}
      <input ref={inputRef} type="file" accept={accept} onChange={handleChange} className="hidden" />
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Timeline filmstrip — Phase 1: display only (no compositing).
// ──────────────────────────────────────────────────────────────────────
function MockShootTimeline({ projectId, authHeaders, aspectClass }) {
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/projects/${projectId}/mock-shoot/timeline`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : null)
      .then(setTimeline)
      .catch(() => {});
  }, [projectId]);

  const ratioCls = aspectClass || 'aspect-video';
  // Portrait clips need a narrower slot so 9:16 doesn't make the filmstrip tower.
  const slotWidthCls = ratioCls === 'aspect-[9/16]' ? 'w-24' : ratioCls === 'aspect-square' ? 'w-32' : 'w-40';

  if (!timeline) return <div className="text-gray-500 text-sm">Loading timeline…</div>;

  return (
    <div className="space-y-3" data-testid="mock-shoot-timeline-view">
      <div className="text-sm text-gray-400">
        Total runtime: <span className="text-white font-mono">{timeline.total_duration_seconds.toFixed(1)}s</span> across {timeline.total_shots} shots
      </div>
      <div className="overflow-x-auto pb-3">
        <div className="flex gap-2 min-w-max">
          {timeline.items.map(it => (
            <div key={it.shot_id} className={`flex-shrink-0 ${slotWidthCls} space-y-1`} data-testid={`timeline-clip-${it.shot_id}`}>
              {it.has_video ? (
                <a href={it.mock_video_url} target="_blank" rel="noreferrer" className={`block relative ${ratioCls} bg-black rounded overflow-hidden`}>
                  {it.mock_video_thumbnail_url ? (
                    <img src={it.mock_video_thumbnail_url} alt={it.shot_number} className="w-full h-full object-cover" />
                  ) : (
                    <Film className="w-8 h-8 text-gray-600 absolute inset-0 m-auto" />
                  )}
                  <PlayCircle className="absolute inset-0 m-auto w-10 h-10 text-white/80" />
                  {it.mock_video_duration ? (
                    <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[10px] rounded">
                      {it.mock_video_duration.toFixed(1)}s
                    </span>
                  ) : null}
                </a>
              ) : (
                <div className={`${ratioCls} border-2 border-dashed border-gray-700 rounded flex items-center justify-center text-gray-600 text-xs`}>
                  no clip
                </div>
              )}
              <div className="text-[11px] font-mono text-cyan-400">{it.scene_number}-{it.shot_number}</div>
            </div>
          ))}
          {timeline.items.length === 0 && (
            <div className="text-gray-500 text-sm py-12">No shots yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Lock-banner — small reusable banner for downstream stages.
// ──────────────────────────────────────────────────────────────────────
export function MockShootLockBanner({ projectId, currentUser, onGoToMockShoot }) {
  const [progress, setProgress] = useState(null);
  const token = (() => { try { return localStorage.getItem('token') || ''; } catch { return ''; } })();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetch(`${API}/api/projects/${projectId}/mock-shoot/progress`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : null)
      .then(setProgress)
      .catch(() => {});
  }, [projectId]);

  if (!progress || progress.unlocked) return null;

  return (
    <div className="rounded-lg border border-amber-700/50 bg-amber-950/30 p-4 flex items-center justify-between gap-4" data-testid="mock-shoot-lock-banner">
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-amber-400" />
        <div>
          <div className="text-amber-200 font-medium text-sm">This stage is locked until Mock Shoot is complete.</div>
          <div className="text-amber-400/80 text-xs mt-0.5">
            {progress.both_uploaded} of {progress.total_shots} shots uploaded · upload an image + video clip for every shot.
          </div>
        </div>
      </div>
      <button
        onClick={onGoToMockShoot}
        className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded"
        data-testid="mock-shoot-banner-goto"
      >Go to Mock Shoot →</button>
    </div>
  );
}
